import { useCustomerStore } from 'stores/customer.store';

import clsx from 'clsx';
import React, { useRef, useState } from 'react';

import { Dialog, DialogContent, DialogOverlay, DialogTitle } from '@radix-ui/react-dialog';

import { IconBackArrow, IconCloseCircle } from '@/assets/icons';
import { useLocationParams } from '@/hooks/useLocationParams';
import { Size } from '@/hooks/useScreenSize';
import { useAnalytics } from '@/lib/analytics';
import { ContentType, MODAL_DEVICE_INFO_TYPE, MODAL_TYPE } from '@/lib/constants';
import { MODAL_INFO_TYPE } from '@/lib/notification-message';
import { newDialogBoxSize } from '@/lib/utils';

import HeaderSection from '../../discovery/dialog-bottom/header-section';
import { scrollContainerClassMap } from '../cart-bottom-dialog/style';
import { ScrollContainer } from '../scroll-container';
import {
  addressCheckerContentPositionClassMap,
  commonForLRBSpacingClassMap,
  commonForLRSpacingClassMap,
  commonSpacingClassMap,
  contentPostionClassMap,
  descriptionClassMap,
  dialogContainerClassMap,
  dialogContentClassMap,
  dialogContentPositionClassMap,
  iconClassMap,
  iconContainerClassMap,
  notificationIconContainerClassMap,
  stickyContainerClassMap,
} from './style';

interface T_Props {
  isOpen: boolean;
  children?: React.ReactNode;
  size: Size;
  resetModal: () => void;
  title?: string;
  description?: string;
  isCloseShow?: boolean;
  thumbnail?: { url: string };
  isCommonHeaderSection?: boolean;
  isShortLine?: boolean;
  contentClassName?: string;
  type?: string;
  isTitle?: boolean;
  isScrollIcon?: boolean;
  deviceType?: string;
  childContentClassName?: string;
  isCrossIconSpacing?: boolean;
  removeCloseOnScroll?: boolean;
  onCloseIconClick?: () => void;
}

export const ConfirmationDialog: React.FC<React.HTMLAttributes<HTMLDivElement> & T_Props> = ({
  isOpen = true,
  children,
  size = 'sm',
  resetModal,
  title,
  description,
  thumbnail,
  isCloseShow = true,
  isCommonHeaderSection = true,
  isShortLine = false,
  contentClassName = '',
  type,
  isTitle = true,
  isScrollIcon = false,
  deviceType,
  childContentClassName = '',
  isCrossIconSpacing = true,
  removeCloseOnScroll = false,
  onCloseIconClick,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [showScrollIcon, setShowScrollIcon] = useState<boolean>(false);
  size = newDialogBoxSize(size);
  const { customerFailureCode } = useCustomerStore(state => state);
  const { trackCtaClicked } = useAnalytics();
  const { tab } = useLocationParams();

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    setIsScrolled(scrollTop > 0);
    setShowScrollIcon(scrollTop > 100);
  };

  const scrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isStickyHeader = title || thumbnail;

  const isHeaderHiddenType = (type: any) =>
    type && [MODAL_TYPE.DEVICE_SECTION, MODAL_TYPE.DEVICE_INFO].includes(type);

  const IsModalCheck = (type: string): boolean => {
    const modalTypesCopy = [...Object.values(MODAL_INFO_TYPE)];
    const validModalTypes = new Set<string>(
      modalTypesCopy.filter(modalType => modalType !== MODAL_INFO_TYPE.REMOVE_PLAN)
    );

    return MODAL_DEVICE_INFO_TYPE.SKY_BOOSTER !== deviceType && validModalTypes.has(type);
  };
  const isModalCheckForSpacing = (type: any) =>
    [MODAL_TYPE.MORE_INFO, MODAL_TYPE.CHANNEL_INFO].includes(type);

  const getSpacingClassName = (type: any) => {
    if (MODAL_DEVICE_INFO_TYPE.SKY_BOOSTER === deviceType) return;

    if (type === MODAL_INFO_TYPE.REMOVE_PLAN) {
      return ['lg', 'xl', 'xxl'].includes(size) ? commonSpacingClassMap[size] : '';
    }

    if (IsModalCheck(type)) {
      return commonSpacingClassMap[size];
    } else if (isModalCheckForSpacing(type)) {
      return 'pb-spacing-10xl';
    } else if (type === MODAL_TYPE.TERMS_AND_CONDITION || type === MODAL_TYPE.QUICK_COMPARE) {
      return commonForLRSpacingClassMap[size];
    } else if (
      [MODAL_TYPE.SKY_HOME_PHONE_NUMBER, MODAL_TYPE.SKY_HOME_PHONE_PROVIDER].includes(type)
    ) {
      return '!pb-0';
    } else {
      return commonForLRBSpacingClassMap[size];
    }
  };

  const iconBgColorMap = (type: any) => {
    return isHeaderHiddenType(type) ? 'bg-color-background-overlay-primary' : '';
  };

  const dialogContentScrollClass =
    'min-h-0 flex-1 overflow-x-hidden overflow-y-auto [scrollbar-gutter:stable] custom-scroll-bar dialog-scroll-bar';

  const gaTrackCTAClickEvent = () => {
    const popupNames: Record<string, string> = {
      [MODAL_INFO_TYPE.EXISTING_USER_VERIFY]: 'Close Popup Existing Customer',
      [MODAL_INFO_TYPE.BLOCKED_USER]: 'Close Popup Former Customer',
      BLOCK_USER_NEVER: 'Close Popup Never Customer',
    };

    let modalType =
      customerFailureCode === 0 || customerFailureCode === '0' ? 'BLOCK_USER_NEVER' : type;
    const name = modalType ? (popupNames[modalType] ?? 'Close Popup') : 'Close Popup';
    trackCtaClicked({
      link_text: undefined,
      link_url: undefined,
      component: ContentType.POPUP,
      context: `checkout/${tab}`,
      name,
      outbound: false,
    });
  };

  const showCloseIcon = isCloseShow && !(removeCloseOnScroll && isScrolled);

  return (
    <div>
      {isOpen ? (
        <Dialog open={isOpen} key={`dialog-${type}`}>
          <DialogOverlay
            data-testid="dialogOverlay"
            className={clsx(dialogContainerClassMap[size])}
          >
            <div className={contentPostionClassMap[size]} aria-label="Dialog Container">
              <DialogContent aria-describedby="" className={clsx(dialogContentClassMap[size])}>
                <DialogTitle className="hidden"></DialogTitle>
                <div
                  className={clsx(
                    dialogContentPositionClassMap[size],
                    contentClassName,
                    'flex min-h-0 w-full max-w-full flex-1 flex-col overflow-hidden'
                  )}
                  aria-label="Dialog Content Container"
                >
                  <div
                    className={clsx(
                      isTitle ? 'relative min-w-full' : addressCheckerContentPositionClassMap[size],
                      'flex min-h-0 w-full max-w-full flex-1 flex-col overflow-hidden'
                    )}
                    aria-label="Dialog Content Wrapper"
                  >
                    {isCommonHeaderSection ? (
                      <div
                        className={clsx(
                          stickyContainerClassMap[size],
                          'w-full max-w-full',
                          {
                            'shadow-custom-one bg-color-background-overlay-primary':
                              isScrolled && isStickyHeader,
                          },
                          { 'bg-transparent': !isStickyHeader }
                        )}
                        aria-label="Dialog Header"
                      >
                        {showCloseIcon ? (
                          <div
                            className={clsx(
                              isCrossIconSpacing
                                ? [iconContainerClassMap, iconBgColorMap(type)]
                                : notificationIconContainerClassMap[size]
                            )}
                            aria-label="Close"
                          >
                            <IconCloseCircle
                              className={clsx(iconClassMap[size], 'cursor-pointer')}
                              onClick={() => {
                                gaTrackCTAClickEvent();
                                onCloseIconClick?.();
                                resetModal();
                              }}
                            />
                          </div>
                        ) : null}
                        {!showCloseIcon && type === MODAL_INFO_TYPE.ESTIMATE_BILL_INFO && (
                          <div aria-label="Back" className="fixed left-[16px] top-[16px] lg:hidden">
                            <div className="flex h-[27px] items-center sm:pb-[4.5px] sm:pl-[2px] sm:pr-[5px] sm:pt-[5px] md:h-[35px] md:pb-[5.75px] md:pl-[2.6px] md:pr-[6.4px] md:pt-[6.5px] lg:hidden">
                              <IconBackArrow
                                className="h-[17.502px] w-[20px] cursor-pointer text-color-background-icons-main-primary md:h-[22.752px] md:w-[26px]"
                                onClick={() => {
                                  gaTrackCTAClickEvent();
                                  resetModal();
                                }}
                              />
                            </div>
                          </div>
                        )}
                        {!isHeaderHiddenType(type) && (
                          <HeaderSection
                            thumbnail={thumbnail}
                            title={title}
                            handleReset={() => {
                              gaTrackCTAClickEvent();
                              resetModal();
                            }}
                            isShortLine={isShortLine}
                            size={size}
                            type={type}
                          />
                        )}
                        {description ? (
                          <h2 className={descriptionClassMap[size]}>{description}</h2>
                        ) : null}
                      </div>
                    ) : null}

                    <div
                      className={clsx(
                        childContentClassName,
                        getSpacingClassName(type),
                        dialogContentScrollClass,
                        'pb-spacing-10xl'
                      )}
                      aria-label="Dialog Content"
                      onScroll={handleScroll}
                      ref={contentRef}
                    >
                      {children}

                      <div
                        onClick={scrollToTop}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            scrollToTop();
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        className={clsx('mt-spacing-5xl flex flex-col items-center', {
                          hidden: !isScrollIcon || !showScrollIcon,
                        })}
                      >
                        <ScrollContainer
                          isRotate={true}
                          className={scrollContainerClassMap[size]}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </div>
          </DialogOverlay>
        </Dialog>
      ) : null}
    </div>
  );
};
